import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './LoginForm.module.css';

const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const { login, loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Rol bazlı yönlendirme: önce geldiği sayfa (from) varsa oraya dön,
    // yoksa admin -> admin panel, diğer -> profile
    const redirectByRole = () => {
        const from = location.state?.from;
        if (from) {
            navigate(from, { replace: true });
            return;
        }
        let role = '';
        try {
            role = JSON.parse(localStorage.getItem('user') || '{}').role || '';
        } catch {
            role = '';
        }
        navigate(role === 'admin' ? '/admin/dashboard' : '/profile');
    };

    useEffect(() => {
        // URL'den gelen mesajı kontrol et
        if (location.state && location.state.message) {
            setMessage(location.state.message);
            
            // Kayıt başarılıysa, kullanıcı bilgilerini otomatik doldur
            if (location.state.user) {
                setEmail(location.state.user.email || '');
            }
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const ok = await login(email, password);
            if (ok) {
                redirectByRole();
            } else {
                setError('Email, nom d\'utilisateur ou mot de passe incorrect');
            }
        } catch (err) {
            console.error('Login error:', err);
            
            // Hata mesajını daha detaylı gösterelim
            let errorMessage = 'Erreur lors de la connexion';
            
            if (err.response) {
                console.error('Error response:', err.response);
                console.error('Error data:', err.response.data);
                errorMessage = err.response.data?.detail || errorMessage;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            console.error('Error details:', errorMessage);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setError('');
            setLoading(true);
            
            // Google ile giriş yap
            const result = await loginWithGoogle();
            
            if (result) {
                // Başarılı giriş sonrası rol bazlı yönlendir
                redirectByRole();
            } else {
                // Kullanıcı popup'ı kapattı veya işlem iptal edildi
                // Sessizce geç, hata gösterme
                setLoading(false);
            }
        } catch (err) {
            console.error('Google login error:', err);
            
            // Hata mesajını göster
            if (err.code === 'auth/popup-blocked') {
                setError('Votre navigateur a bloqué la fenêtre popup. Veuillez autoriser les popups pour ce site.');
            } else if (err.code === 'auth/unauthorized-domain') {
                setError('Domaine non autorisé pour la connexion Google. Veuillez contacter l’administrateur.');
            } else {
                setError(err.response?.data?.detail || 'Une erreur est survenue lors de la connexion avec Google.');
            }
            
            setLoading(false);
        }
    };

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginForm}>
                <h2 className={styles.title}>Connexion</h2>
                
                {message && <div className={styles.message}>{message}</div>}
                {error && <div className={styles.error}>{error}</div>}
                
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">Email ou nom d'utilisateur</label>
                        <input
                            type="text"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    
                    <div className={styles.formGroup}>
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>
                    
                    <button 
                        type="submit" 
                        className={styles.submitButton}
                        disabled={loading}
                    >
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                </form>
                
                <div className={styles.divider}>
                    <span>ou</span>
                </div>
                
                <button 
                    type="button"
                    onClick={handleGoogleLogin}
                    className={styles.googleButton}
                    disabled={loading}
                >
                    <img 
                        src="https://developers.google.com/identity/images/g-logo.png" 
                        alt="Google logo" 
                        className={styles.googleLogo} 
                    />
                    Se connecter avec Google
                </button>
                
                <div className={styles.links}>
                    <p>
                        Vous n'avez pas de compte? <Link to="/register">S'inscrire</Link>
                    </p>
                    <p>
                        <Link to="/forgot-password">Mot de passe oublié?</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
